import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Globe, Mail, Phone, Calendar, Users, Award, ExternalLink, ChevronRight } from 'lucide-react';
import { SUPPLIERS } from '@/data/suppliers';
import { PRODUCTS } from '@/data/products';
import { getSupplierById, getProductsBySupplier, ROUTE_PATHS } from '@/lib/index';
import { ProductCard } from '@/components/ProductCard';
import { AdBanner } from '@/components/AdBanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useState } from 'react';

export default function SupplierDetail() {
  const { id } = useParams<{ id: string }>();
  const supplier = getSupplierById(SUPPLIERS, id || '');
  const supplierProducts = getProductsBySupplier(PRODUCTS, id || '');
  const relatedSuppliers = SUPPLIERS.filter(s => s.id !== id && s.categories.some(cat => supplier?.categories.includes(cat))).slice(0, 3);
  
  const [selectedImage, setSelectedImage] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });

  if (!supplier) {
    return <Navigate to={ROUTE_PATHS.SUPPLIERS} replace />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    alert('Thank you for your inquiry. We will contact you soon.');
    setFormData({ name: '', email: '', company: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-gradient-to-b from-primary/5 via-background to-background py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link to={ROUTE_PATHS.HOME} className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to={ROUTE_PATHS.SUPPLIERS} className="hover:text-primary transition-colors">Suppliers</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-foreground">{supplier.name}</span>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Card className="overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-6 mb-8">
                      <img
                        src={supplier.logo}
                        alt={supplier.name}
                        className="w-24 h-24 object-cover rounded-lg border border-border"
                      />
                      <div className="flex-1">
                        <h1 className="text-3xl font-bold mb-2">{supplier.name}</h1>
                        <p className="text-muted-foreground mb-4">{supplier.shortDescription}</p>
                        <div className="flex flex-wrap gap-2">
                          {supplier.categories.map((category) => (
                            <Badge key={category} variant="secondary">{category}</Badge>
                          ))}
                          {supplier.featured && (
                            <Badge className="bg-accent text-accent-foreground">Featured Supplier</Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 mb-8">
                      <div className="flex items-center gap-3 text-sm">
                        <MapPin className="w-5 h-5 text-primary" />
                        <div>
                          <div className="font-medium">Location</div>
                          <div className="text-muted-foreground">{supplier.location}, {supplier.country}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Calendar className="w-5 h-5 text-primary" />
                        <div>
                          <div className="font-medium">Founded</div>
                          <div className="text-muted-foreground">{supplier.founded}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Users className="w-5 h-5 text-primary" />
                        <div>
                          <div className="font-medium">Employees</div>
                          <div className="text-muted-foreground">{supplier.employees}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Award className="w-5 h-5 text-primary" />
                        <div>
                          <div className="font-medium">Certifications</div>
                          <div className="text-muted-foreground">{supplier.certifications.length} Active</div>
                        </div>
                      </div>
                    </div>

                    <Separator className="my-8" />

                    <Tabs defaultValue="overview" className="w-full">
                      <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="products">Products</TabsTrigger>
                        <TabsTrigger value="gallery">Gallery</TabsTrigger>
                        <TabsTrigger value="certifications">Certifications</TabsTrigger>
                      </TabsList>

                      <TabsContent value="overview" className="mt-6">
                        <div className="prose prose-sm max-w-none">
                          <h3 className="text-xl font-semibold mb-4">Company Overview</h3>
                          <p className="text-muted-foreground leading-relaxed">{supplier.description}</p>
                          
                          <h3 className="text-xl font-semibold mt-8 mb-4">Product Categories</h3>
                          <ul className="space-y-2">
                            {supplier.products.map((product, index) => (
                              <li key={index} className="flex items-center gap-2">
                                <ChevronRight className="w-4 h-4 text-primary" />
                                <span>{product}</span>
                              </li>
                            ))}
                          </ul>

                          {supplier.videoUrl && (
                            <div className="mt-8">
                              <h3 className="text-xl font-semibold mb-4">Company Video</h3>
                              <div className="aspect-video rounded-lg overflow-hidden">
                                <iframe
                                  src={supplier.videoUrl}
                                  className="w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="products" className="mt-6">
                        {supplierProducts.length > 0 ? (
                          <div className="grid md:grid-cols-2 gap-6">
                            {supplierProducts.map((product) => (
                              <ProductCard key={product.id} product={product} />
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <p className="text-muted-foreground">No products available at the moment.</p>
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="gallery" className="mt-6">
                        <div className="space-y-6">
                          <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                            <img
                              src={supplier.images[selectedImage]}
                              alt={`${supplier.name} - Image ${selectedImage + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="grid grid-cols-4 gap-4">
                            {supplier.images.map((image, index) => (
                              <button
                                key={index}
                                onClick={() => setSelectedImage(index)}
                                className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                                  selectedImage === index ? 'border-primary' : 'border-transparent hover:border-border'
                                }`}
                              >
                                <img
                                  src={image}
                                  alt={`Thumbnail ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="certifications" className="mt-6">
                        <div className="grid sm:grid-cols-2 gap-4">
                          {supplier.certifications.map((cert, index) => (
                            <Card key={index}>
                              <CardContent className="p-6">
                                <div className="flex items-center gap-3">
                                  <Award className="w-8 h-8 text-primary" />
                                  <div>
                                    <div className="font-semibold">{cert}</div>
                                    <div className="text-sm text-muted-foreground">Active Certification</div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>

                {relatedSuppliers.length > 0 && (
                  <div className="mt-8">
                    <h2 className="text-2xl font-bold mb-6">Related Suppliers</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                      {relatedSuppliers.map((related) => (
                        <Card key={related.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                          <CardContent className="p-6">
                            <img
                              src={related.logo}
                              alt={related.name}
                              className="w-full h-32 object-cover rounded-lg mb-4"
                            />
                            <h3 className="font-semibold mb-2">{related.name}</h3>
                            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{related.shortDescription}</p>
                            <Link to={`/suppliers/${related.id}`}>
                              <Button variant="outline" size="sm" className="w-full">
                                View Profile
                              </Button>
                            </Link>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                    <div className="space-y-4">
                      <a
                        href={supplier.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                      >
                        <Globe className="w-5 h-5" />
                        <span>Visit Website</span>
                        <ExternalLink className="w-4 h-4 ml-auto" />
                      </a>
                      <a
                        href={`mailto:${supplier.email}`}
                        className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                      >
                        <Mail className="w-5 h-5" />
                        <span>{supplier.email}</span>
                      </a>
                      <a
                        href={`tel:${supplier.phone}`}
                        className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                      >
                        <Phone className="w-5 h-5" />
                        <span>{supplier.phone}</span>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Send Inquiry</h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="company">Company</Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          rows={4}
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full">
                        Send Inquiry
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <AdBanner size="rectangle" position="supplier-detail-sidebar" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
